import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Match, 
  MatchCreate,
  Court, 
  Club, 
  Tournament, 
  TournamentParticipant,
  MatchSuggestion,
  MatchSuggestionCreate,
  SoloTournamentLeaderboardEntry,
  Score, 
  AdminStats, 
  UserStats, 
  MatchStats,
  LoginCredentials,
  AuthResponse,
  TimeslotRule,
  TimeslotException,
  TimeslotPreviewSlot,
  ExternalIntegrationConfig,
  ExternalIntegrationStatus,
} from '../types';
import {
  EmailLog,
  EmailTemplate,
  EmailStats,
  EmailListResponse,
  SendEmailRequest,
  SendBulkEmailRequest,
  SendEventEmailRequest,
} from '../types/email';
import { getConfig } from '../config/environment';

// Get API URL - HTTPS for production, HTTP for local dev (localhost/192.168.x.x)
const getApiBaseUrl = () => {
  const config = getConfig();
  return config.API_URL;
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const initialBaseURL = getApiBaseUrl();
    this.api = axios.create({
      baseURL: initialBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (requestConfig) => {
        requestConfig.baseURL = getApiBaseUrl();
        const token = localStorage.getItem('access_token');
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        
        return requestConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle token refresh and Mixed Content errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Log Mixed Content errors on production (HTTPS page + HTTP API)
        if (error.message && (
          error.message.includes('Mixed Content') ||
          error.message.includes('insecure') ||
          error.message.includes('blocked')
        )) {
          console.error('❌ Mixed Content Error:', error.message);
        }

        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              // Use this.api.post - getApiBaseUrl() returns correct URL (HTTP for dev, HTTPS for prod)
              const response = await this.api.post(
                '/auth/refresh',
                { refresh_token: refreshToken }
              );
              const { access_token, refresh_token: newRefreshToken } = response.data;
              localStorage.setItem('access_token', access_token);
              if (newRefreshToken) {
                localStorage.setItem('refresh_token', newRefreshToken);
              }
              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return this.api(originalRequest);
            }
                  } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Don't redirect automatically, let the component handle it
          console.warn('Token refresh failed:', refreshError);
        }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> {
    return this.api.post('/auth/login', credentials);
  }

  async refreshToken(refreshToken: string): Promise<AxiosResponse<AuthResponse>> {
    return this.api.post('/auth/refresh', { refresh_token: refreshToken });
  }

  async getSsoToken(): Promise<AxiosResponse<{ sso_token: string }>> {
    return this.api.post('/auth/sso-token');
  }

  async resetPassword(token: string, newPassword: string): Promise<AxiosResponse<{ message: string }>> {
    return this.api.post('/auth/reset-password', { token, new_password: newPassword });
  }

  async getCurrentUser(): Promise<AxiosResponse<User>> {
    return this.api.get('/users/me');
  }

  async updateCurrentUser(userData: Partial<User>): Promise<AxiosResponse<User>> {
    return this.api.put('/users/me', userData);
  }

  async deleteCurrentUser(): Promise<AxiosResponse<void>> {
    return this.api.delete('/users/me');
  }

  // Get users for regular users (from matches)
  async getUsersFromMatches(): Promise<AxiosResponse<any>> {
    return this.api.get('/matches/');
  }

  // Match actions
  async joinMatch(matchId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/matches/${matchId}/join`);
  }

  async leaveMatch(matchId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/matches/${matchId}/leave`);
  }

  async getMatches(params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    tournament_id?: number;
    is_tournament_match?: boolean;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<Match[]>> {
    return this.api.get('/matches/', { params });
  }

  async getAllMatches(params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    tournament_id?: number;
    is_tournament_match?: boolean;
    limit?: number;
  }): Promise<AxiosResponse<Match[]>> {
    return this.getMatches({ page: 1, limit: Math.min(params?.limit ?? 100, 100), ...params });
  }

  async getMatch(matchId: number): Promise<AxiosResponse<Match>> {
    return this.api.get(`/matches/${matchId}`);
  }

  // Admin stats endpoints
  async getAdminStats(period: string = 'all_time'): Promise<AxiosResponse<AdminStats>> {
    return this.api.get(`/admin/stats?period=${period}`);
  }

  async getUserStats(period: string = 'all_time', limit: number = 20): Promise<AxiosResponse<UserStats>> {
    return this.api.get(`/admin/stats/users?period=${period}&limit=${limit}`);
  }

  async getMatchStats(period: string = 'all_time'): Promise<AxiosResponse<MatchStats>> {
    return this.api.get(`/admin/stats/matches?period=${period}`);
  }

  async getAdminStatsUsers(period: string = 'all_time'): Promise<AxiosResponse<UserStats>> {
    return this.api.get(`/admin/stats/users?period=${period}`);
  }

  async getAdminStatsMatches(period: string = 'all_time'): Promise<AxiosResponse<MatchStats>> {
    return this.api.get(`/admin/stats/matches?period=${period}`);
  }

  // User management endpoints
  async getAllUsers(): Promise<AxiosResponse<User[]>> {
    // Use /admin/users endpoint which returns all users without pagination
    return this.api.get('/admin/users');
  }

  async getUser(userId: number): Promise<AxiosResponse<User>> {
    return this.api.get(`/users/${userId}`);
  }

  async getPublicUserProfile(userId: number): Promise<
    AxiosResponse<{
      id: number;
      name: string;
      avatar_url?: string | null;
      initial_elo?: number | null;
      elo_rating?: number | null;
      total_matches: number;
      total_wins: number;
      win_rate: number;
      preferred_side?: string | null;
      club_name?: string | null;
    }>
  > {
    return this.api.get(`/users/${userId}/public`);
  }

  async createUser(userData: Partial<User>): Promise<AxiosResponse<User>> {
    return this.api.post('/users/', userData);
  }

  async updateUser(userId: number, userData: Partial<User>): Promise<AxiosResponse<User>> {
    return this.api.put(`/users/${userId}`, userData);
  }

  async deleteUser(userId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/users/${userId}`);
  }

  /** GET /statistics/users/{user_id}/matches - user's match list for progress/charts */
  async getStatisticsUserMatches(userId: number): Promise<
    AxiosResponse<{
      user_id: number;
      total_matches: number;
      completed_matches: number;
      upcoming_matches: number;
      matches: Array<{ match_id: number; date: string | null; status: string }>;
    }>
  > {
    return this.api.get(`/statistics/users/${userId}/matches`);
  }

  /** GET /statistics/users/{user_id}/performance - win rate, streaks, recent matches */
  async getStatisticsUserPerformance(userId: number): Promise<
    AxiosResponse<{
      user_id: number;
      win_rate: number;
      total_matches: number;
      total_wins: number;
      total_losses: number;
      average_score: number;
      performance: {
        current_streak: number;
        best_streak: number;
        recent_matches: Array<{
          match_id: number;
          date: string;
          won: boolean;
          team1_score: number;
          team2_score: number;
          user_team: number;
          winner_team: number;
        }>;
      };
    }>
  > {
    return this.api.get(`/statistics/users/${userId}/performance`);
  }

  // Match management endpoints
  async createMatch(matchData: MatchCreate): Promise<AxiosResponse<Match>> {
    return this.api.post('/matches/', matchData);
  }

  async updateMatch(matchId: number, matchData: Partial<Match>): Promise<AxiosResponse<Match>> {
    return this.api.put(`/matches/${matchId}/`, matchData);
  }

  async deleteMatch(matchId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/matches/${matchId}/`);
  }

  async completeMatch(matchId: number, resultData: any): Promise<AxiosResponse<Match>> {
    return this.api.put(`/admin/matches/${matchId}/complete`, resultData);
  }

  async assignPlayersToMatch(matchId: number, players: Array<{ user_id: number; team_number: number; position: string }>): Promise<AxiosResponse<Match>> {
    return this.api.post(`/admin/matches/${matchId}/assign-players`, { players });
  }

  async removePlayerFromMatch(matchId: number, userId: number): Promise<AxiosResponse<Match>> {
    return this.api.delete(`/admin/matches/${matchId}/remove-player`, {
      params: { user_id: userId },
    });
  }

  // Court management endpoints
  async getAllCourts(): Promise<AxiosResponse<Court[]>> {
    return this.api.get('/courts/all');
  }

  async getCourts(clubIds?: number[]): Promise<AxiosResponse<Court[]>> {
    const params = clubIds?.length ? { club_ids: clubIds.join(',') } : {};
    return this.api.get('/courts/', { params });
  }

  async createCourt(courtData: Partial<Court>): Promise<AxiosResponse<Court>> {
    return this.api.post('/courts/', courtData);
  }

  async updateCourt(courtId: number, courtData: Partial<Court>): Promise<AxiosResponse<Court>> {
    return this.api.put(`/courts/${courtId}`, courtData);
  }

  async deleteCourt(courtId: number, cascade = false): Promise<AxiosResponse<void>> {
    return this.api.delete(`/courts/${courtId}`, { params: cascade ? { cascade: true } : {} });
  }

  async updateCourtPricing(courtId: number, pricingData: { hourly_rate: number; peak_hour_rate: number; weekend_rate: number; member_discount?: number }): Promise<AxiosResponse<Court>> {
    return this.api.put(`/courts/${courtId}/pricing`, pricingData);
  }

  // Club management endpoints
  async getAllClubs(): Promise<AxiosResponse<Club[]>> {
    return this.api.get('/clubs/');
  }

  async getClubs(): Promise<AxiosResponse<Club[]>> {
    return this.api.get('/clubs/');
  }

  async createClub(clubData: Partial<Club>): Promise<AxiosResponse<Club>> {
    return this.api.post('/clubs/', clubData);
  }

  async updateClub(clubId: number, clubData: Partial<Club>): Promise<AxiosResponse<Club>> {
    return this.api.put(`/clubs/${clubId}`, clubData);
  }

  async deleteClub(clubId: number, cascade = false): Promise<AxiosResponse<void>> {
    return this.api.delete(`/clubs/${clubId}`, { params: cascade ? { cascade: true } : {} });
  }

  async uploadClubLogo(clubId: number, file: File): Promise<AxiosResponse<Club>> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.api.post(`/clubs/${clubId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // External integration endpoints
  async enableExternalIntegration(clubId: number, config: ExternalIntegrationConfig): Promise<AxiosResponse<Club>> {
    return this.api.post(`/clubs/${clubId}/external-integration`, config);
  }

  async updateExternalIntegration(clubId: number, config: ExternalIntegrationConfig): Promise<AxiosResponse<Club>> {
    return this.api.put(`/clubs/${clubId}/external-integration`, config);
  }

  async disableExternalIntegration(clubId: number): Promise<AxiosResponse<Club>> {
    return this.api.delete(`/clubs/${clubId}/external-integration`);
  }

  async syncExternalIntegration(clubId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/clubs/${clubId}/external-integration/sync`);
  }

  async getExternalIntegrationStatus(clubId: number): Promise<AxiosResponse<ExternalIntegrationStatus>> {
    return this.api.get(`/clubs/${clubId}/external-integration/status`);
  }

  // Tournament management endpoints
  async getTimeslotRules(clubId: number, courtId?: number): Promise<AxiosResponse<TimeslotRule[]>> {
    return this.api.get(`/admin/clubs/${clubId}/timeslot-rules`, { params: { court_id: courtId } });
  }

  async createTimeslotRule(ruleData: Partial<TimeslotRule>): Promise<AxiosResponse<TimeslotRule>> {
    return this.api.post('/admin/timeslot-rules', ruleData);
  }

  async updateTimeslotRule(ruleId: number, ruleData: Partial<TimeslotRule>): Promise<AxiosResponse<TimeslotRule>> {
    return this.api.put(`/admin/timeslot-rules/${ruleId}`, ruleData);
  }

  async deleteTimeslotRule(ruleId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/admin/timeslot-rules/${ruleId}`);
  }

  async getTimeslotExceptions(clubId: number, courtId?: number): Promise<AxiosResponse<TimeslotException[]>> {
    return this.api.get(`/admin/clubs/${clubId}/timeslot-exceptions`, { params: { court_id: courtId } });
  }

  async createTimeslotException(exceptionData: Partial<TimeslotException>): Promise<AxiosResponse<TimeslotException>> {
    return this.api.post('/admin/timeslot-exceptions', exceptionData);
  }

  async updateTimeslotException(exceptionId: number, exceptionData: Partial<TimeslotException>): Promise<AxiosResponse<TimeslotException>> {
    return this.api.put(`/admin/timeslot-exceptions/${exceptionId}`, exceptionData);
  }

  async deleteTimeslotException(exceptionId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/admin/timeslot-exceptions/${exceptionId}`);
  }

  async previewTimeslots(params: { club_id: number; court_id: number; date_from: string; date_to: string }): Promise<AxiosResponse<{ slots: TimeslotPreviewSlot[] }>> {
    return this.api.get('/admin/timeslots/preview', { params });
  }

  async getTimeSlots(params: { court_id?: number; date_from?: string; date_to?: string; include_booked?: boolean }): Promise<AxiosResponse<any[]>> {
    return this.api.get('/time-slots', { params });
  }

  async getTimeSlotsByClub(clubId: number, params: { date_from: string; date_to: string; include_booked?: boolean }): Promise<AxiosResponse<any[]>> {
    try {
      // Get all courts for the club first, then fetch slots for each court
      const courtsResponse = await this.api.get('/courts/all');
      const data: any = courtsResponse.data;
      const courts = Array.isArray(data) ? data : (data?.courts || data?.data || []);
      const clubCourts = courts.filter((court: any) => court.club_id === clubId);
      
      if (clubCourts.length === 0) {
        return { data: [] } as unknown as AxiosResponse<any[]>;
      }
      
      // Fetch slots for all courts in parallel
      const slotPromises = clubCourts.map((court: any) => 
        this.api.get('/time-slots', { 
          params: { 
            court_id: court.id, 
            date_from: params.date_from, 
            date_to: params.date_to,
            include_booked: params.include_booked 
          } 
        }).catch((err) => {
          // If one court fails, return empty array for that court
          console.warn(`Failed to fetch slots for court ${court.id}:`, err);
          return { data: [] } as unknown as AxiosResponse<any[]>;
        })
      );
      
      const responses = await Promise.all(slotPromises);
      const allSlots = responses.flatMap(response => Array.isArray(response.data) ? response.data : []);
      
      return { data: allSlots } as unknown as AxiosResponse<any[]>;
    } catch (error) {
      console.error('Error fetching time slots by club:', error);
      return { data: [] } as unknown as AxiosResponse<any[]>;
    }
  }

  async blockTimeSlot(timeSlotId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/time-slots/${timeSlotId}/block`);
  }

  async unblockTimeSlot(timeSlotId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/time-slots/${timeSlotId}/unblock`);
  }

  async bookTimeSlot(timeSlotId: number, bookingType: string = 'match'): Promise<AxiosResponse<any>> {
    return this.api.post(`/time-slots/${timeSlotId}/book`, { booking_type: bookingType });
  }

  async cancelBooking(timeSlotId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/time-slots/${timeSlotId}/cancel`);
  }

  async getMyBookings(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/time-slots/my-bookings');
  }

  async deleteTimeSlot(timeSlotId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/admin/time-slots/${timeSlotId}`);
  }

  async deleteTimeSlotsByRange(params: { court_id: number; date_from: string; date_to: string; only_available?: boolean }): Promise<AxiosResponse<{ message: string; count: number }>> {
    return this.api.delete('/admin/time-slots', { params });
  }

  // Tournaments endpoints
  async getAllTournaments(): Promise<AxiosResponse<Tournament[]>> {
    return this.api.get('/tournaments/');
  }

  async getTournaments(): Promise<AxiosResponse<Tournament[]>> {
    return this.api.get('/tournaments/');
  }

  async getTournament(tournamentId: number): Promise<AxiosResponse<Tournament>> {
    return this.api.get(`/tournaments/${tournamentId}`);
  }

  async createTournament(tournamentData: Partial<Tournament>): Promise<AxiosResponse<Tournament>> {
    return this.api.post('/tournaments/', tournamentData);
  }

  async updateTournament(tournamentId: number, tournamentData: Partial<Tournament>): Promise<AxiosResponse<Tournament>> {
    return this.api.put(`/tournaments/${tournamentId}`, tournamentData);
  }

  async deleteTournament(tournamentId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/tournaments/${tournamentId}`);
  }

  async registerForTournament(tournamentId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/tournaments/${tournamentId}/register`);
  }

  async unregisterFromTournament(tournamentId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/tournaments/${tournamentId}/unregister`);
  }

  async claimTournamentInvite(token: string): Promise<AxiosResponse<any>> {
    return this.api.post(`/tournaments/invites/${token}/claim`);
  }

  async getTournamentParticipants(tournamentId: number): Promise<AxiosResponse<TournamentParticipant[]>> {
    return this.api.get(`/tournaments/${tournamentId}/participants`);
  }

  async approveTournamentParticipant(tournamentId: number, participantId: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/tournaments/${tournamentId}/participants/${participantId}/approve`);
  }

  async rejectTournamentParticipant(tournamentId: number, participantId: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/tournaments/${tournamentId}/participants/${participantId}/reject`);
  }

  async removeTournamentParticipant(tournamentId: number, participantId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/tournaments/${tournamentId}/participants/${participantId}`);
  }

  async getTournamentOrganizers(tournamentId: number): Promise<AxiosResponse<{ user_id: number; user_name?: string; added_at?: string }[]>> {
    return this.api.get(`/tournaments/${tournamentId}/organizers`);
  }

  async addTournamentOrganizer(tournamentId: number, userId: number): Promise<AxiosResponse<{ user_id: number; user_name?: string; added_at?: string }>> {
    return this.api.post(`/tournaments/${tournamentId}/organizers`, { user_id: userId });
  }

  async removeTournamentOrganizer(tournamentId: number, userId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/tournaments/${tournamentId}/organizers/${userId}`);
  }

  async addTournamentParticipant(tournamentId: number, participantData: { player1_id: number; player2_id?: number; team_name?: string; notes?: string }): Promise<AxiosResponse<TournamentParticipant>> {
    return this.api.post(`/tournaments/${tournamentId}/participants`, participantData);
  }

  async getTournamentLeaderboard(tournamentId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/tournaments/${tournamentId}/leaderboard`);
  }

  async getSoloTournamentLeaderboard(tournamentId: number): Promise<AxiosResponse<SoloTournamentLeaderboardEntry[]>> {
    return this.api.get(`/tournaments/${tournamentId}/solo-leaderboard`);
  }

  async getFlexLeagueLeaderboard(tournamentId: number): Promise<AxiosResponse<SoloTournamentLeaderboardEntry[]>> {
    return this.api.get(`/tournaments/${tournamentId}/flex-leaderboard`);
  }

  async getTournamentRanking(tournamentId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/tournaments/${tournamentId}/ranking`);
  }

  async getTournamentBracket(tournamentId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/tournaments/${tournamentId}/bracket`);
  }

  async getTournamentLadder(tournamentId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/tournaments/${tournamentId}/ladder`);
  }

  async applyToFlexLeagueTournament(tournamentId: number, data: { notes?: string }): Promise<AxiosResponse<any>> {
    return this.api.post(`/tournaments/${tournamentId}/apply`, data);
  }

  async joinTournament(tournamentId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/tournaments/${tournamentId}/join`);
  }

  async leaveTournament(tournamentId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/tournaments/${tournamentId}/leave`);
  }

  // Score management endpoints
  // REMOVED: getScores() method - use getAllScores() for admin or getUserScores(userId) for user scores
  // This method was removed to prevent calls to the non-existent /scores/ endpoint

  // Get scores for a specific user
  async getUserScores(userId: number): Promise<AxiosResponse<{ user_id: number; scores: Score[] }>> {
    return this.api.get(`/scores/users/${userId}/scores`);
  }

  // Admin endpoint to get all scores
  async getAllScores(): Promise<AxiosResponse<Score[]>> {
    return this.api.get('/admin/scores/');
  }

  async createScore(scoreData: Partial<Score>): Promise<AxiosResponse<Score>> {
    return this.api.post('/scores/', scoreData);
  }

  async updateScore(scoreId: number, scoreData: Partial<Score>): Promise<AxiosResponse<Score>> {
    return this.api.put(`/scores/scores/${scoreId}`, scoreData);
  }

  // Admin method to update score
  async adminUpdateScore(scoreId: number, scoreData: Partial<Score>): Promise<AxiosResponse<Score>> {
    return this.api.put(`/admin/scores/scores/${scoreId}`, scoreData);
  }

  async deleteScore(scoreId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/scores/scores/${scoreId}`);
  }

  // Admin method to delete score
  async adminDeleteScore(scoreId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/scores/scores/${scoreId}`);
  }

  async adminSubmitScore(matchId: number, scoreData: Partial<Score>): Promise<AxiosResponse<Score>> {
    return this.api.post(`/admin/scores/matches/${matchId}/submit`, scoreData);
  }

  /** GET /scores/matches/{match_id}/scores - get scores for a match */
  async getMatchScores(matchId: number): Promise<AxiosResponse<{ match_id: number; scores: Score[] }>> {
    return this.api.get(`/scores/matches/${matchId}/scores`);
  }

  // Match suggestions endpoints
  async getMatchSuggestions(tournamentId?: number): Promise<AxiosResponse<MatchSuggestion[]>> {
    if (tournamentId) {
      return this.api.get(`/match-suggestions/`, { params: { tournament_id: tournamentId } });
    }
    return this.api.get('/match-suggestions/');
  }

  async createMatchSuggestion(tournamentId: number, suggestionData: MatchSuggestionCreate): Promise<AxiosResponse<MatchSuggestion>> {
    return this.api.post('/match-suggestions/', { ...suggestionData, tournament_id: tournamentId });
  }

  async acceptMatchSuggestion(suggestionId: number): Promise<AxiosResponse<Match>> {
    return this.api.post(`/match-suggestions/${suggestionId}/accept`);
  }

  async approveMatchSuggestion(tournamentId: number, suggestionId: number): Promise<AxiosResponse<Match>> {
    return this.api.post(`/match-suggestions/${suggestionId}/approve`, { tournament_id: tournamentId });
  }

  async rejectMatchSuggestion(tournamentId: number, suggestionId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/match-suggestions/${suggestionId}`, { params: { tournament_id: tournamentId } });
  }

  // Notification endpoints
  async getNotifications(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/notifications/');
  }

  async markNotificationAsRead(notificationId: number): Promise<AxiosResponse<void>> {
    return this.api.put(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead(): Promise<AxiosResponse<void>> {
    return this.api.put('/notifications/read-all');
  }

  async sendNotification(notificationData: { user_id: number; title: string; body: string; data?: any }): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/notifications', notificationData);
  }

  async sendNotificationToAllUsers(notificationData: { title: string; body: string; data?: any }): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/notifications/broadcast', notificationData);
  }

  async testNotification(): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/notifications/test');
  }

  // System endpoints
  async getSystemHealth(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/system/health');
  }

  async getSystemLogs(): Promise<AxiosResponse<any>> {
    // This endpoint doesn't exist yet - return empty response without making the call
    // Keeping method for future implementation
    return Promise.resolve({ 
      data: { logs: [] }, 
      status: 200, 
      statusText: 'OK', 
      headers: {}, 
      config: {} as any 
    } as AxiosResponse<any>);
  }

  // Tournament match management
  async getTournamentMatches(tournamentId: number): Promise<AxiosResponse<Match[]>> {
    return this.api.get(`/tournaments/${tournamentId}/matches`);
  }

  async canLaunchMontanteRound(tournamentId: number): Promise<AxiosResponse<{ can_launch: boolean; reason?: string; next_round: number }>> {
    return this.api.get(`/tournaments/${tournamentId}/montante/can-launch-round`);
  }

  async launchMontanteRound(tournamentId: number): Promise<AxiosResponse<{ round_number: number; matches_created: number; match_ids: number[] }>> {
    return this.api.post(`/tournaments/${tournamentId}/montante/launch-round`);
  }

  async canLaunchMexicanoRound(tournamentId: number): Promise<AxiosResponse<{ can_launch: boolean; reason?: string; next_round: number }>> {
    return this.api.get(`/tournaments/${tournamentId}/mexicano/can-launch-round`);
  }

  async launchMexicanoRound(tournamentId: number): Promise<AxiosResponse<{ round_number: number; matches_created: number; match_ids: number[]; players_seated?: number; players_sitting_out?: number }>> {
    return this.api.post(`/tournaments/${tournamentId}/mexicano/launch-round`);
  }

  async canLaunchAmericanoRound(tournamentId: number): Promise<AxiosResponse<{ can_launch: boolean; reason?: string; next_round: number }>> {
    return this.api.get(`/tournaments/${tournamentId}/americano/can-launch-round`);
  }

  async launchAmericanoRound(tournamentId: number): Promise<AxiosResponse<{ round_number: number; matches_created: number; match_ids: number[]; players_seated?: number; players_sitting_out?: number }>> {
    return this.api.post(`/tournaments/${tournamentId}/americano/launch-round`);
  }

  async approveTournamentMatch(matchId: number): Promise<AxiosResponse<{ message: string }>> {
    return this.api.put(`/admin/matches/${matchId}/approve`);
  }

  async rejectTournamentMatch(matchId: number, reason?: string): Promise<AxiosResponse<{ message: string }>> {
    return this.api.put(`/admin/matches/${matchId}/reject`, { reason: reason || 'No reason provided' });
  }

  async seedData(): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/system/seed');
  }

  async resetAllData(): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/reset-all-data');
  }

  async createFictiveData(numUsers: number = 20, matchesPerUser: number = 25): Promise<AxiosResponse<any>> {
    return this.api.post(`/admin/create-fictive-data?num_users=${numUsers}&matches_per_user=${matchesPerUser}`);
  }

  async createBackup(backupType: string = 'full'): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/system/backup', { type: backupType });
  }

  async systemBackup(backupType: string = 'full'): Promise<AxiosResponse<any>> {
    return this.createBackup(backupType);
  }

  async systemRestart(): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/system/restart');
  }

  // Feature flags endpoints
  async getFeatureFlags(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/admin/feature-flags');
  }

  async createFeatureFlag(flagData: { name: string; description?: string; is_active: boolean }): Promise<AxiosResponse<any>> {
    return this.api.post('/admin/feature-flags', flagData);
  }

  async updateFeatureFlag(flagId: number, flagData: { name?: string; description?: string; is_active?: boolean }): Promise<AxiosResponse<any>> {
    return this.api.put(`/admin/feature-flags/${flagId}`, flagData);
  }

  async deleteFeatureFlag(flagId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/admin/feature-flags/${flagId}`);
  }

  async getUserFeatureFlags(userId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/admin/feature-flags/users/${userId}`);
  }

  async grantFeatureFlagToUser(userId: number, flagId: number): Promise<AxiosResponse<any>> {
    return this.api.post(`/admin/feature-flags/users/${userId}/${flagId}`);
  }

  async revokeFeatureFlagFromUser(userId: number, flagId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/admin/feature-flags/users/${userId}/${flagId}`);
  }

  async getMyFeatureFlags(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/users/me/feature-flags');
  }

  // Email Management Methods
  async getEmailLogs(params?: {
    page?: number;
    limit?: number;
    template_name?: string;
    status?: string;
    recipient_email?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<EmailListResponse> {
    const response = await this.api.get<EmailListResponse>('/admin/emails', { params });
    return response.data;
  }

  async getEmailLog(emailId: number): Promise<EmailLog> {
    const response = await this.api.get<EmailLog>(`/admin/emails/${emailId}`);
    return response.data;
  }

  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const response = await this.api.get<EmailTemplate[]>('/admin/emails/templates/list');
    return response.data;
  }

  async sendEmail(request: SendEmailRequest): Promise<EmailLog> {
    const response = await this.api.post<EmailLog>('/admin/emails/send', request);
    return response.data;
  }

  async sendBulkEmail(request: SendBulkEmailRequest): Promise<EmailLog[]> {
    const response = await this.api.post<EmailLog[]>('/admin/emails/send-bulk', request);
    return response.data;
  }

  async sendEventEmail(request: SendEventEmailRequest): Promise<EmailLog[]> {
    const response = await this.api.post<EmailLog[]>('/admin/emails/send-event', request);
    return response.data;
  }

  async getEmailStats(period?: string): Promise<EmailStats> {
    const response = await this.api.get<EmailStats>('/admin/emails/stats/summary', {
      params: { period },
    });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService; 
